import {
  capitalizeTerm,
  isNotificationActionUrl,
  NOTIFICATION_ACTION_LABEL_MAX,
  NOTIFICATION_BODY_MAX,
  NOTIFICATION_TERM_TOKENS,
  NOTIFICATION_TITLE_MAX,
  type NotificationAction,
  type NotificationCategory,
  type NotificationContent,
  type NotificationIcon,
  type NotificationRenderContext,
  type NotificationTemplateOverride,
  type NotificationType,
  notificationTemplateIssue,
  type ResolvedNotificationTemplate,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
// Type-only import: the dashboard never bundles server code.
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import { type LocalSchedule, localScheduleToIso } from "./localSchedule";

type NotificationsProxy = TRPCOptionsProxy<AppRouter>["notifications"];

/** One system type as `notifications.templates` lists it. */
export type NotificationTemplateRow = inferOutput<
  NotificationsProxy["templates"]
>["templates"][number];

/** A marketing notification as `notifications.campaigns` lists it. */
export type NotificationCampaign = inferOutput<NotificationsProxy["campaigns"]>["data"][number];

// ---- the draft both editors hold -------------------------------------------------------------

/** What the template editor and the composer edit: text as typed, the link kept while switched off. */
export interface NotificationDraft {
  title: string;
  body: string;
  icon: NotificationIcon;
  /** Whether the notification links anywhere; off keeps the label and URL for switching back. */
  linked: boolean;
  actionLabel: string;
  actionUrl: string;
  /** "" = no banner. */
  imageUrl: string;
}

/** The draft of a template or a campaign's content as it stands. */
export function draftFromContent(
  content: Pick<NotificationContent, "title" | "body" | "icon" | "action" | "imageUrl">,
): NotificationDraft {
  return {
    title: content.title,
    body: content.body,
    icon: content.icon,
    linked: content.action !== null,
    actionLabel: content.action?.label ?? "",
    actionUrl: content.action?.url ?? "",
    imageUrl: content.imageUrl ?? "",
  };
}

/** A new marketing notification's starting point. */
export const EMPTY_NOTIFICATION_DRAFT: NotificationDraft = {
  title: "",
  body: "",
  icon: "megaphone",
  linked: false,
  actionLabel: "",
  actionUrl: "",
  imageUrl: "",
};

/** The draft's link, trimmed; null when it links nowhere. */
export function draftAction(draft: NotificationDraft): NotificationAction | null {
  if (!draft.linked) return null;
  return { label: draft.actionLabel.trim(), url: draft.actionUrl.trim() };
}

/** The draft as campaign content, trimmed. */
export function draftToContent(draft: NotificationDraft): NotificationContent {
  return {
    title: draft.title.trim(),
    body: draft.body.trim(),
    icon: draft.icon,
    action: draftAction(draft),
    imageUrl: draft.imageUrl.trim() || null,
  };
}

/** The draft as a resolved template, for the preview. */
export function draftToTemplate(
  draft: NotificationDraft,
  enabled: boolean,
): ResolvedNotificationTemplate {
  return { enabled, ...draftToContent(draft) };
}

function sameAction(a: NotificationAction | null, b: NotificationAction | null): boolean {
  if (a === null || b === null) return a === b;
  return a.label === b.label && a.url === b.url;
}

/**
 * The override to save for a system type: only what differs from the default, so a field left at
 * its default keeps following it. `{}` means the template is the default.
 */
export function templateOverride(
  draft: NotificationDraft,
  enabled: boolean,
  defaults: ResolvedNotificationTemplate,
): NotificationTemplateOverride {
  const content = draftToContent(draft);
  const override: NotificationTemplateOverride = {};
  if (enabled !== defaults.enabled) override.enabled = enabled;
  if (content.title !== defaults.title) override.title = content.title;
  if (content.body !== defaults.body) override.body = content.body;
  if (content.icon !== defaults.icon) override.icon = content.icon;
  if (!sameAction(content.action, defaults.action)) override.action = content.action;
  if (content.imageUrl !== defaults.imageUrl) override.imageUrl = content.imageUrl;
  return override;
}

/** Whether an override changes nothing, so saving it is a reset. */
export function isDefaultOverride(override: NotificationTemplateOverride): boolean {
  return Object.keys(override).length === 0;
}

/** Whether two drafts would save the same thing. */
export function sameDraft(a: NotificationDraft, b: NotificationDraft): boolean {
  const x = draftToContent(a);
  const y = draftToContent(b);
  return (
    x.title === y.title &&
    x.body === y.body &&
    x.icon === y.icon &&
    sameAction(x.action, y.action) &&
    x.imageUrl === y.imageUrl
  );
}

// ---- validation ------------------------------------------------------------------------------

export type NotificationDraftField = "title" | "body" | "actionLabel" | "actionUrl" | "imageUrl";

export type NotificationDraftIssues = Partial<Record<NotificationDraftField, string>>;

/** The most characters an action link may have (`notificationActionSchema`). */
const ACTION_URL_MAX = 500;
/** The most characters an image link may have. */
const IMAGE_URL_MAX = 2000;

function textIssue(
  value: string,
  opts: { empty: string; max: number; variables: readonly string[] },
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return opts.empty;
  if (trimmed.length > opts.max) return `${opts.max} characters at most.`;
  return notificationTemplateIssue(trimmed, opts.variables) ?? undefined;
}

/** An action link as the contract stores it: a storefront path or an https link. */
function actionUrlIssue(url: string, variables: readonly string[]): string | undefined {
  const trimmed = url.trim();
  if (!trimmed) return "Add where the button goes.";
  if (trimmed.length > ACTION_URL_MAX) return `${ACTION_URL_MAX} characters at most.`;
  const placeholderIssue = notificationTemplateIssue(trimmed, variables);
  if (placeholderIssue) return placeholderIssue;
  // Placeholders are filled when it renders, so the link is checked with each standing in as "x".
  const sample = trimmed.replace(/\{[A-Za-z][A-Za-z0-9]*\}/g, "x");
  const allowed =
    (trimmed.startsWith("/") || trimmed.startsWith("https://")) && isNotificationActionUrl(sample);
  return allowed ? undefined : "Use a storefront path like /boxes or an https:// link.";
}

function imageUrlIssue(url: string): string | undefined {
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > IMAGE_URL_MAX) return `${IMAGE_URL_MAX} characters at most.`;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === "https:" || protocol === "http:" ? undefined : "Use an https:// link.";
  } catch {
    return "Use an https:// link, or upload an image.";
  }
}

/**
 * Why each field of a draft would be refused, by field: empty, too long, or a placeholder the
 * notification does not have (`variables`, none for a marketing one).
 */
export function notificationDraftIssues(
  draft: NotificationDraft,
  variables: readonly string[] = [],
): NotificationDraftIssues {
  const issues: NotificationDraftIssues = {};
  const note = (field: NotificationDraftField, issue: string | undefined) => {
    if (issue) issues[field] = issue;
  };
  note(
    "title",
    textIssue(draft.title, { empty: "Add a title.", max: NOTIFICATION_TITLE_MAX, variables }),
  );
  note(
    "body",
    textIssue(draft.body, { empty: "Add a message.", max: NOTIFICATION_BODY_MAX, variables }),
  );
  if (draft.linked) {
    note(
      "actionLabel",
      textIssue(draft.actionLabel, {
        empty: "Name the button.",
        max: NOTIFICATION_ACTION_LABEL_MAX,
        variables,
      }),
    );
    note("actionUrl", actionUrlIssue(draft.actionUrl, variables));
  }
  note("imageUrl", imageUrlIssue(draft.imageUrl));
  return issues;
}

/** Whether a draft has nothing to fix. */
export function hasNoIssues(issues: NotificationDraftIssues): boolean {
  return Object.keys(issues).length === 0;
}

// ---- placeholders ----------------------------------------------------------------------------

/** One chip under the text fields: the token it inserts and what it reads as. */
export interface PlaceholderChip {
  token: string;
  /** What the chip says beside the token: the variable's meaning, or the word it renders. */
  hint: string;
}

/** The chips an editor offers: the type's own variables, the store's name, then each term. */
export function placeholderChips(
  variables: readonly { name: string; label: string }[],
  context: Pick<NotificationRenderContext, "terms" | "storeName">,
): { details: PlaceholderChip[]; words: PlaceholderChip[] } {
  const words = Object.entries(NOTIFICATION_TERM_TOKENS)
    // The capitalized twins are the same word; the hint under the chips names them.
    .filter(([token]) => token.charAt(0) === token.charAt(0).toLowerCase())
    .map(([token, word]) => ({ token, hint: word(context.terms) }));
  return {
    details: variables.map((variable) => ({ token: variable.name, hint: variable.label })),
    words: [{ token: "storeName", hint: context.storeName }, ...words],
  };
}

/** A text field's caret, or the span it has selected. */
export interface TextSelection {
  start: number;
  end: number;
}

/**
 * `{token}` put into `value` over the selection (at the end when there is none), with the caret
 * after it.
 */
export function insertPlaceholder(
  value: string,
  selection: TextSelection | null,
  token: string,
): { value: string; caret: number } {
  const clamp = (n: number) => Math.max(0, Math.min(value.length, n));
  const start = clamp(selection?.start ?? value.length);
  const end = Math.max(start, clamp(selection?.end ?? start));
  const text = `{${token}}`;
  return { value: value.slice(0, start) + text + value.slice(end), caret: start + text.length };
}

/** A storefront page a link can name without a picker, and the button label it suggests. */
export interface StorefrontPageLink {
  /** The chip's name for the page, in the tenant's words. */
  label: string;
  url: string;
  /** Filled in when the button has no label yet; placeholders, so it follows renames. */
  buttonLabel: string;
}

/** The storefront pages every store has. */
export function storefrontPageLinks(terms: StorefrontTerminology): StorefrontPageLink[] {
  return [
    { label: capitalizeTerm(terms.box.plural), url: "/boxes", buttonLabel: "Browse {boxes}" },
    {
      label: capitalizeTerm(terms.inventory.singular),
      url: "/inventory",
      buttonLabel: "View {inventory}",
    },
    { label: "Account", url: "/account", buttonLabel: "View account" },
  ];
}

/** The link to one box's storefront page, and the label it suggests. */
export function boxPageLink(boxId: string): Pick<StorefrontPageLink, "url" | "buttonLabel"> {
  return { url: `/boxes/${encodeURIComponent(boxId)}`, buttonLabel: "Open {box}" };
}

/** The draft pointed at a page, keeping a label the editor already wrote. */
export function linkDraftTo(
  draft: NotificationDraft,
  link: Pick<StorefrontPageLink, "url" | "buttonLabel">,
): NotificationDraft {
  return {
    ...draft,
    linked: true,
    actionUrl: link.url,
    actionLabel: draft.actionLabel.trim() ? draft.actionLabel : link.buttonLabel,
  };
}

/** A curated icon's name as the picker reads it: "package-x" → "package x". */
export function notificationIconName(icon: string): string {
  return icon.replaceAll("-", " ");
}

// ---- system types ----------------------------------------------------------------------------

/** The system categories, in the dashboard's order: every category but marketing. */
export const SYSTEM_NOTIFICATION_CATEGORIES = [
  "orders",
  "battles",
  "forge",
  "balance",
  "account",
] as const satisfies readonly Exclude<NotificationCategory, "marketing">[];

/** The type the System page shows on its own card: the message a new shopper gets. */
export const WELCOME_NOTIFICATION_TYPE: NotificationType = "welcome";

/** Why an unavailable type is not sent, in the tenant's words. */
export function unavailableNote(
  row: Pick<NotificationTemplateRow, "type" | "category">,
  terms: StorefrontTerminology,
): string {
  if (row.category === "battles") {
    return `Not sent while ${capitalizeTerm(terms.battle.plural)} are off in this environment.`;
  }
  if (row.category === "forge") {
    return `Not sent while ${capitalizeTerm(terms.forge.singular)} is off in this environment.`;
  }
  if (row.type === "balance_purchase_completed" || row.type === "balance_purchase_failed") {
    return "Not sent while balance purchases are off in this environment.";
  }
  return "Not sent in this environment right now.";
}

// ---- recipient lists -------------------------------------------------------------------------

/** The most people one list may name (`NOTIFICATION_CAMPAIGN_MAX_RECIPIENTS`). */
export const RECIPIENT_LIST_MAX = 10_000;

/** A header cell naming a column the server matches on: a Loot OS id, an external id, an email. */
const IDENTIFIER_HEADER =
  /^(e-?mail( address)?|id|external[ _-]?id|(end[ _-]?)?user[ _-]?id|shopper[ _-]?id|customer[ _-]?id)$/i;

function splitCells(line: string): string[] {
  return line.split(/[\t,;]/).map((cell) =>
    cell
      .trim()
      .replace(/^"(.*)"$/, "$1")
      .trim(),
  );
}

/**
 * The identifiers in a pasted list or an uploaded CSV / text file: one per cell, split on lines,
 * commas, semicolons and tabs. A first row that names an id, external id or email column is a
 * header, and then only those columns are read, so a sheet's other columns never become entries.
 * Repeats are dropped and counted.
 */
export function parseRecipientList(text: string): {
  identifiers: string[];
  duplicates: number;
} {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  const first = lines[0] ? splitCells(lines[0]) : [];
  const columns = first.flatMap((cell, index) => (IDENTIFIER_HEADER.test(cell) ? [index] : []));
  const cells =
    columns.length > 0
      ? lines.slice(1).flatMap((line) => {
          const row = splitCells(line);
          return columns.map((index) => row[index] ?? "");
        })
      : lines.flatMap(splitCells);
  const seen = new Set<string>();
  let duplicates = 0;
  for (const cell of cells) {
    if (!cell) continue;
    if (seen.has(cell)) duplicates += 1;
    else seen.add(cell);
  }
  return { identifiers: [...seen], duplicates };
}

// ---- scheduling ------------------------------------------------------------------------------

/** Why a scheduled time is refused, or null: unreadable, or not in the future. */
export function scheduleIssue(schedule: LocalSchedule, now: Date): string | null {
  const iso = localScheduleToIso(schedule);
  if (!iso) return "Pick a time.";
  if (new Date(iso).getTime() <= now.getTime()) return "Pick a time in the future.";
  return null;
}

// ---- campaigns -------------------------------------------------------------------------------

/** Who a campaign reaches, as the list reads it. */
export function campaignAudienceLabel(
  campaign: Pick<NotificationCampaign, "audience" | "recipientCount">,
): string {
  if (campaign.audience === "all") return "All shoppers";
  return `List of ${shoppers(campaign.recipientCount ?? 0)}`;
}

/** Who a campaign reaches, mid-sentence: "all shoppers", "a list of 120 shoppers". */
export function campaignAudiencePhrase(
  campaign: Pick<NotificationCampaign, "audience" | "recipientCount">,
): string {
  if (campaign.audience === "all") return "all shoppers";
  return `a list of ${shoppers(campaign.recipientCount ?? 0)}`;
}

/** When a campaign went out, or will. */
export function campaignTime(campaign: Pick<NotificationCampaign, "sentAt" | "scheduledAt">) {
  return campaign.sentAt ?? campaign.scheduledAt;
}

/** A scheduled campaign whose time has come: the dispatcher sends it within a minute. */
export function isCampaignDue(
  campaign: Pick<NotificationCampaign, "status" | "scheduledAt">,
  now: Date,
): boolean {
  return campaign.status === "scheduled" && Date.parse(campaign.scheduledAt) <= now.getTime();
}

/** What a campaign's row says: `sending` once a scheduled one is due, else its status. */
export function campaignStatusLabel(
  campaign: Pick<NotificationCampaign, "status" | "scheduledAt">,
  now: Date,
): "sent" | "sending" | "scheduled" {
  return isCampaignDue(campaign, now) ? "sending" : campaign.status;
}

function shoppers(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "shopper" : "shoppers"}`;
}

/**
 * The toast after a campaign is saved: sent, scheduled (`scheduledFor`, already formatted), or,
 * for one sent now, on its way (the dispatcher sends it); then how many list entries matched
 * nobody.
 */
export function campaignSavedMessage(args: {
  campaign: Pick<NotificationCampaign, "status" | "deliveredCount">;
  sentNow: boolean;
  scheduledFor: string;
  unmatched: number;
}): string {
  const { campaign } = args;
  const outcome =
    campaign.status === "sent"
      ? `Sent to ${shoppers(campaign.deliveredCount ?? 0)}.`
      : args.sentNow
        ? "Sending: it goes out within a minute."
        : `Scheduled for ${args.scheduledFor}.`;
  if (args.unmatched === 0) return outcome;
  return `${outcome} ${args.unmatched.toLocaleString("en-US")} ${args.unmatched === 1 ? "entry" : "entries"} on the list matched no one.`;
}
